import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'theme';
const THEMES = ['light', 'dark', 'system'];

const prefersDark = () => (
  window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
);

const resolveTheme = (theme) => (theme === 'system' ? (prefersDark() ? 'dark' : 'light') : theme);

const readStoredTheme = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return THEMES.includes(saved) ? saved : 'system';
  } catch {
    return 'system';
  }
};

const writeStoredTheme = (theme) => {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // storage non disponibile (scheda privata, storage bloccato dal browser):
    // il tema vale solo per la sessione corrente.
  }
};

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readStoredTheme);

  const setTheme = useCallback((next) => {
    if (!THEMES.includes(next)) return;
    setThemeState(next);
    writeStoredTheme(next);
  }, []);

  useEffect(() => {
    const apply = () => {
      document.documentElement.setAttribute('data-theme', resolveTheme(theme));
    };
    apply();
    if (theme === 'system' && window.matchMedia) {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      mq.addEventListener('change', apply);
      return () => mq.removeEventListener('change', apply);
    }
    return undefined;
  }, [theme]);

  const value = { theme, setTheme, resolvedTheme: resolveTheme(theme) };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
